import { initializeApp } from 'firebase/app';
import {
  confirmPasswordReset,
  connectAuthEmulator,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  reauthenticateWithCredential,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updatePassword,
  verifyBeforeUpdateEmail,
} from 'firebase/auth';
// import { multiFactor, PhoneAuthProvider, PhoneMultiFactorGenerator, RecaptchaVerifier } from 'firebase/auth';
import { useEffect, useState, useRef } from 'react';
import { useLanguage } from './LanguageContext';
import { ErrorCode } from '../shared/constants/errorCodes';
import { ServerValidationError } from './api';

const waitForFirebaseUser = () =>
  new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(getAuth(), (user) => {
      unsubscribe();
      resolve(user);
    });
  });

const getFirebaseUser = () => getAuth().currentUser;

const refreshFirebaseUser = async () => {
  const firebaseUser = getFirebaseUser();
  if (firebaseUser) {
    await firebaseUser.reload();
    await firebaseUser.getIdToken(true);
  }
};

//AUTH_TODO handle more error codes: https://stackoverflow.com/questions/67617502/what-are-the-error-codes-for-flutter-firebase-auth-exception
const errorCodeMappings = {
  'auth/invalid-email': [{ field: 'email', code: 'invalidEmail' }],
  'auth/email-already-in-use': [{ field: 'email', code: ErrorCode.DUPLICATE_KEY }],
  'auth/weak-password': [{ field: 'password', code: 'tooSimple' }],
  'auth/wrong-password': [{ field: 'password', code: 'invalidCredentials' }],
  'auth/user-not-found': [{ field: 'password', code: 'invalidCredentials' }],
};

const handleValidationErrors = async (fn, fieldRemapping = {}) => {
  try {
    return await fn();
  } catch (error) {
    if (error.code in errorCodeMappings) {
      const errors = errorCodeMappings[error.code];
      const errorsMapped = errors.map(({ field, ...rest }) => ({ field: fieldRemapping[field] ?? field, ...rest }));
      throw new ServerValidationError(errorsMapped);
    }
    throw error;
  }
};

const useFirebase = (params, onInitialization) => {
  const { currentLanguage } = useLanguage();
  const [isReady, setIsReady] = useState(false);
  const initRef = useRef(onInitialization);
  initRef.current = onInitialization;

  useEffect(() => {
    if (!params) return;
    if (params.MARKETING_MODE) {
      setIsReady(true);
      return;
    }

    const firebaseApp = initializeApp({
      projectId: params.googleIdentityProjectId,
      apiKey: params.googleIdentityApiKey,
      authDomain: params.googleIdentityAuthDomain,
    });
    const auth = getAuth(firebaseApp);
    if (params.googleIdentityEmulatorUrl) {
      connectAuthEmulator(auth, params.googleIdentityEmulatorUrl);
    }

    const unsubscribe = onAuthStateChanged(auth, async () => {
      await initRef.current();
      setIsReady(true);
    });
    return unsubscribe;
  }, [params, currentLanguage]);

  const setCurrentLanguageForEmails = () => {
    //TODO: this code which sets the language for the email verifications cannot be tested in the emulator
    getAuth().languageCode = currentLanguage;
  };

  const reauthenticateWithPassword = async (password, passwordField) =>
    await handleValidationErrors(
      async () => {
        const user = getFirebaseUser();
        const credential = EmailAuthProvider.credential(user.email, password);
        await reauthenticateWithCredential(user, credential);
      },
      { password: passwordField },
    );

  const urlFor = (path) => ({ url: `${window.location.origin}/${path}` });

  return {
    isReady,
    getToken: async () => getFirebaseUser()?.getIdToken() ?? null,
    isLoggedIn: async () => {
      await refreshFirebaseUser();
      return !!getFirebaseUser();
    },
    userAuthenticatedUsingPassword: () => getFirebaseUser()?.providerData?.[0]?.providerId === 'password',
    userEmail: () => getFirebaseUser().email,
    userEmailIsVerified: async () => {
      await refreshFirebaseUser();
      return getFirebaseUser().emailVerified;
    },
    createUserWithEmailAndPassword: async (email, password) => {
      await handleValidationErrors(async () => await createUserWithEmailAndPassword(getAuth(), email, password));
      await waitForFirebaseUser();
    },
    sendEmailVerification: async () => {
      setCurrentLanguageForEmails();
      await sendEmailVerification(getFirebaseUser());
    },
    updateUserEmail: async (currentPassword, newEmail) => {
      await reauthenticateWithPassword(currentPassword, 'password');
      await handleValidationErrors(
        async () => await verifyBeforeUpdateEmail(getFirebaseUser(), newEmail, urlFor('updateEmail')),
      );
    },
    updateUserPassword: async (currentPassword, newPassword) => {
      await reauthenticateWithPassword(currentPassword, 'currentPassword');
      await handleValidationErrors(async () => await updatePassword(getFirebaseUser(), newPassword), {
        password: 'newPassword',
      });
    },
    sendPasswordResetEmail: async (email) => {
      await handleValidationErrors(async () => {
        try {
          await sendPasswordResetEmail(getAuth(), email, urlFor('resetPassword'));
        } catch (error) {
          if (error.code === 'auth/user-not-found') {
            // Do nothing
            // Note: ideally the firebase emulator would match prod, where the reset email flow always appears successful,
            // even if the user does not exist. this is to replicate that in testing and in prod it won't have effect
            // Reference: https://github.com/firebase/firebase-tools/issues/6687
          } else {
            throw error;
          }
        }
      });
    },
    confirmPasswordReset: async (oobCode, newPassword) => {
      return await handleValidationErrors(
        async () => {
          try {
            await confirmPasswordReset(getAuth(), oobCode, newPassword);
          } catch (error) {
            if (error.code === 'auth/invalid-action-code') {
              return false; //not successful because invalid code
            } else {
              throw error;
            }
          }
          return true; //successful
        },
        { password: 'newPassword' },
      );
    },
    signInWithEmailAndPassword: async (email, password) => {
      await handleValidationErrors(async () => await signInWithEmailAndPassword(getAuth(), email, password));
      await waitForFirebaseUser();
    },
    signInWithGoogle: async () => {
      await signInWithPopup(getAuth(), new GoogleAuthProvider());
      await waitForFirebaseUser();
    },
    signOut: async () => {
      await signOut(getAuth());
      await waitForFirebaseUser();
    },
  };
};

/*

  const initiateVerifyPhoneNumber = async (recaptchaContainerid, phoneNumberBeforeFormatting) => {
    const formatPhoneNumber = (phone) => {
      const cleaned = phone.replace(/\D/g, '');
      if (!cleaned.startsWith('+')) {
        return `+${cleaned}`;
      }
      return cleaned.match(/^\+\d{10,15}$/) ? cleaned : null;
    };

    // console.log('in initiateVerifyPhoneNumber', { recaptchaContainerid, phoneNumberBeforeFormatting });

    const phoneNumber = formatPhoneNumber(phoneNumberBeforeFormatting);
    // console.log({ phoneNumber });

    const recaptchaVerifier = new RecaptchaVerifier(firebaseAuth, recaptchaContainerid, undefined);

    // console.log({ recaptchaVerifier });

    const multiFactorSession = await multiFactor(getFirebaseUser).getSession();
    // console.log({ multiFactorSession });

    const phoneInfoOptions = {
      phoneNumber: phoneNumber,
      session: multiFactorSession,
    };

    const phoneAuthProvider = new PhoneAuthProvider(firebaseAuth);
    // console.log({ phoneAuthProvider });
    const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier);
    // console.log({ verificationId });
    return verificationId;
  };

  const completeVerifyPhoneNumber = async (verificationId, activationCode) => {
    // console.log('in completeVerifyPhoneNumber', { verificationId, activationCode });
    const cred = PhoneAuthProvider.credential(verificationId, activationCode);

    // console.log({ cred });
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
    // console.log({ multiFactorAssertion });
    const result = multiFactor(getFirebaseUser).enroll(multiFactorAssertion, 'mfaDisplayName');
    // console.log({ result });
    return result;
  };



*/

export { useFirebase };
